/* =============================================================================
   Folders — the ONE reading of an attachment's `groupKey` as a path.

   A document's materials have always carried a `groupKey` ("Namakam",
   "Camakam"), drawn as a titled section of the materials browser. A folder is
   the same field read one level deeper: `Namakam/Anuvāka 1` is the section
   "Namakam" holding a folder "Anuvāka 1", and a folder may hold folders of its
   own. No table, no second field, nothing for an old row to migrate — a key
   without a `/` means exactly what it meant before.

   Everything that reads the path goes through here — the browser, the zip
   route, the input schema — so the page, the link and the download cannot
   disagree about where a file lives.
   ============================================================================= */
export const FOLDER_SEPARATOR = '/';
/** `groupKey` → its path segments. Blank segments are dropped, so `a//b/` is `a/b`. */
export function folderPath(groupKey) {
    if (!groupKey)
        return [];
    return groupKey
        .split(FOLDER_SEPARATOR)
        .map((s) => s.replace(/\s+/g, ' ').trim())
        .filter((s) => s.length > 0);
}
/** The canonical stored form of a path — what `groupKey` should hold. Null when empty. */
export function folderKey(path) {
    return path.length > 0 ? path.join(FOLDER_SEPARATOR) : null;
}
/** Normalise a typed `groupKey` ("Namakam / Anuvāka 1 /") to its canonical form. */
export function normalizeFolderKey(raw) {
    return folderKey(folderPath(raw));
}
/**
 * One segment as it appears in a URL: ASCII, lowercase, hyphenated. Diacritics
 * are folded (`Anuvāka 1` → `anuvaka-1`) so a link survives being typed, pasted
 * into a chat, or read aloud — the Library's slugs follow the same rule.
 */
export function folderSegmentSlug(segment) {
    return segment
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
/** A whole path as a URL value: `namakam/anuvaka-1`. */
export function folderSlug(path) {
    return path.map(folderSegmentSlug).join(FOLDER_SEPARATOR);
}
/** A URL value back to slugs — tolerant of stray slashes and case. */
export function parseFolderSlug(raw) {
    if (!raw)
        return [];
    return raw
        .split(FOLDER_SEPARATOR)
        .map((s) => folderSegmentSlug(s))
        .filter((s) => s.length > 0);
}
/** Does `path` lie inside (or equal) the folder named by `slugs`? */
export function isInFolder(path, slugs) {
    if (slugs.length > path.length)
        return false;
    return slugs.every((s, i) => folderSegmentSlug(path[i]) === s);
}
/**
 * Resolve URL slugs to the folder's real names, using the paths that actually
 * exist. Null when nothing lives there — a renamed or removed folder — so the
 * caller can say so instead of showing an empty page that looks like a bug.
 */
export function resolveFolder(paths, slugs) {
    if (slugs.length === 0)
        return [];
    for (const p of paths) {
        if (isInFolder(p, slugs))
            return p.slice(0, slugs.length);
    }
    return null;
}
